import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Flex, Typography } from 'antd';
import CustomButton from '@/components/CustomButton';
import CustomInput from '@/components/CustomInput';
import CustomDeleteModal from '@/pages/Modals/CustomDeleteModal';
import './SlackConnectionPanel.scss';
import {
  getSlackConnection,
  updateSlackConnection,
} from '@/apis/slackConnections';
import type {
  SlackConnectionAction,
  SlackConnectionStatus,
} from '@/apis/slackConnections';

interface Props {
  teamId: string;
  token: string;
  onChange: () => void;
}

export default function SlackConnectionPanel({
  teamId,
  token,
  onChange,
}: Props) {
  const [status, setStatus] = useState<SlackConnectionStatus | null>(null);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reconnecting, setReconnecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmation, setConfirmation] = useState<
    'test' | 'disconnect' | null
  >(null);

  const reload = useCallback(async () => {
    try {
      const response = await getSlackConnection(token, teamId);
      if (!response.data.data.is_success) throw new Error();
      setStatus(response.data.data.data);
      setError('');
    } catch {
      setError(
        'Slack settings require company administrator access. Try refreshing if you are an administrator.'
      );
    }
  }, [token, teamId]);
  useEffect(() => {
    reload().catch((): void => {});
  }, [reload]);

  const act = async (action: SlackConnectionAction) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await updateSlackConnection(token, teamId, action);
      if (!response.data.data.is_success) throw new Error();
      if (action.action === 'invite') {
        setLink(response.data.data.data.installation_url || '');
        setCopied(false);
      } else {
        setLink('');
        setReconnecting(false);
      }
      if (action.action === 'test')
        setNotice(
          'Test notification queued. Check the selected Slack channel.'
        );
      if (action.action === 'disconnect')
        setNotice(
          'Zoomlogi has stopped sending. Remove the authorization in Slack to revoke it there.'
        );
      await reload();
      onChange();
    } catch {
      setError(
        'Unable to update Slack. Refresh and try again. Setup may be unavailable or the installation link may have expired.'
      );
    } finally {
      setBusy(false);
    }
  };

  const showSetup =
    status?.setup_enabled &&
    !status.pending &&
    !link &&
    (!status.connected || reconnecting);

  return (
    <Flex
      vertical
      gap={12}
      aria-label='Slack connection'
      className='slack-connection fs-exclude rr-block rr-ignore'
    >
      {error && <Alert type='error' message={error} />}
      {notice && <Alert type='info' message={notice} />}
      {!status && !error && (
        <Typography.Text>Loading Slack connection…</Typography.Text>
      )}
      {status && (
        <>
          <Typography.Text
            className='slack-connection__status'
            data-connected={status.connected}
          >
            {status.connected
              ? `Connected${status.channel_name ? ` to ${status.workspace_name} / ${status.channel_name}` : ' using an existing webhook'}`
              : status.status === 'reconnect_required'
                ? 'Slack needs to be reconnected.'
                : 'Slack is not connected.'}
          </Typography.Text>
          {status.pending && (
            <Flex vertical gap={12} className='slack-connection__card'>
              <Typography.Text strong>
                Channel awaiting confirmation
              </Typography.Text>
              <Typography.Text className='slack-connection__hint'>
                Only confirm if this workspace and channel belong to this team.
              </Typography.Text>
              <Typography.Text className='slack-connection__destination'>
                {status.pending.workspace_name} ({status.pending.workspace_id})
                / {status.pending.channel_name} ({status.pending.channel_id})
              </Typography.Text>
              <CustomButton
                text='Confirm this channel'
                className='slack-connection__primary'
                bgColor='var(--text-primary)'
                color='var(--surface-container)'
                height={36}
                borderRadius='8px'
                fontSize='12px'
                fontWeight={600}
                block
                disabled={busy}
                onClick={() => {
                  act({
                    action: 'confirm',
                    invite_id: status.pending!.id,
                  }).catch((): void => {});
                }}
              />
              <Button
                disabled={busy}
                onClick={() => {
                  act({ action: 'cancel' }).catch((): void => {});
                }}
              >
                Cancel setup
              </Button>
            </Flex>
          )}
          {showSetup && (
            <Flex vertical gap={8}>
              <Typography.Text className='slack-connection__hint'>
                {status.connected
                  ? 'Choose a replacement channel. The current connection stays active until you confirm the new one.'
                  : 'Create a link to choose a Slack workspace and channel for this team. You’ll review the connection before notifications start.'}
              </Typography.Text>
              <CustomButton
                text={
                  status.connected
                    ? 'Create reconnect link'
                    : 'Create installation link'
                }
                className='slack-connection__primary'
                bgColor='var(--text-primary)'
                color='var(--surface-container)'
                height={36}
                borderRadius='8px'
                fontSize='12px'
                fontWeight={600}
                block
                disabled={busy}
                onClick={() => {
                  act({ action: 'invite' }).catch((): void => {});
                }}
              />
              {reconnecting && (
                <Button disabled={busy} onClick={() => setReconnecting(false)}>
                  Keep current connection
                </Button>
              )}
            </Flex>
          )}
          {!status.setup_enabled && (
            <Typography.Text className='slack-connection__hint'>
              Slack setup is unavailable. Contact Zoomlogi support for help.
            </Typography.Text>
          )}
          {link && !status.pending && (
            <Flex vertical gap={10} className='slack-connection__card'>
              <Typography.Text strong>Installation link ready</Typography.Text>
              <Typography.Text className='slack-connection__hint'>
                Open this link to choose a channel, or share it with your Slack
                administrator. The link expires in 7 days.
              </Typography.Text>
              <CustomInput
                aria-label='Slack installation link'
                className='statsig-no-capture'
                classNames={{ input: 'statsig-no-capture' }}
                readOnly
                value={link}
              />
              <CustomButton
                text={copied ? 'Link copied' : 'Copy installation link'}
                className='slack-connection__primary'
                bgColor='var(--text-primary)'
                color='var(--surface-container)'
                height={36}
                borderRadius='8px'
                fontSize='12px'
                fontWeight={600}
                block
                onClick={() => {
                  if (!navigator.clipboard) {
                    setError('Select and copy the installation link above.');
                    return;
                  }
                  navigator.clipboard
                    .writeText(link)
                    .then(() => setCopied(true))
                    .catch(() =>
                      setError(
                        'Unable to copy. Select and copy the installation link above.'
                      )
                    );
                }}
              />
              <Typography.Text className='slack-connection__hint'>
                After a channel is selected, refresh below to review and confirm
                it.
              </Typography.Text>
              <Button
                disabled={busy}
                onClick={() => {
                  act({ action: 'cancel' }).catch((): void => {});
                }}
              >
                Cancel installation link
              </Button>
            </Flex>
          )}
          <Flex gap={8} wrap>
            {status.connected &&
              status.setup_enabled &&
              !reconnecting &&
              !link &&
              !status.pending && (
                <Button disabled={busy} onClick={() => setReconnecting(true)}>
                  Create reconnect link
                </Button>
              )}
            {(!status.connected ||
              reconnecting ||
              Boolean(link) ||
              Boolean(status.pending) ||
              Boolean(error)) && (
              <Button
                disabled={busy}
                onClick={() => {
                  reload().catch((): void => {});
                }}
              >
                Refresh Slack status
              </Button>
            )}
            {status.connected && (
              <Button disabled={busy} onClick={() => setConfirmation('test')}>
                Send test notification
              </Button>
            )}
            {(status.connected || status.status === 'reconnect_required') && (
              <Button
                danger
                disabled={busy}
                onClick={() => setConfirmation('disconnect')}
              >
                Disconnect Slack
              </Button>
            )}
          </Flex>
        </>
      )}
      {!status && error && (
        <Button
          onClick={() => {
            reload().catch((): void => {});
          }}
        >
          Refresh Slack status
        </Button>
      )}
      {confirmation && (
        <CustomDeleteModal
          visible
          onClose={() => setConfirmation(null)}
          onConfirm={() => {
            if (busy) return;
            setConfirmation(null);
            act({ action: confirmation }).catch((): void => {});
          }}
          title={
            confirmation === 'disconnect'
              ? 'Disconnect Slack?'
              : 'Send a test notification?'
          }
          message={
            confirmation === 'disconnect'
              ? 'Stop Slack notifications for this team. To revoke access in Slack too, remove the app authorization in Slack.'
              : 'Send a test notification to the connected Slack channel.'
          }
          cancelText='Cancel'
          confirmText={
            confirmation === 'disconnect' ? 'Disconnect Slack' : 'Send test'
          }
          confirmColor={
            confirmation === 'disconnect'
              ? 'var(--danger)'
              : 'var(--text-primary)'
          }
          disableConfirmBtn={busy}
        />
      )}
    </Flex>
  );
}
